import React from "react";
import { Link } from "react-router-dom";
import { AIPageShell, AIDiagram } from "./DualAIShared";
import weddingImage from "./assets/ai-engeeningr/mariage.png";
import hotelImage from "./assets/ai-engeeningr/hotel.png";
import realEstateImage from "./assets/ai-engeeningr/realestate.jpg";
import footImage from "./assets/ai-engeeningr/foot.png";
import multimodelImage from "./assets/ai-engeeningr/multimodel.png";

const runtimeSteps = [
  ["01", "Intent capture", "Capture user goal, output type, target audience, channel and current workflow step before a prompt is assembled."],
  ["02", "Context assembly", "Attach project metadata, retrieved evidence, slot definitions, visual references and permission boundaries for the task."],
  ["03", "Prompt orchestration", "Generate a task-specific instruction with clear formatting rules, tool context and bounded transformation scope."],
  ["04", "Model execution", "Run the best-suited model for ideation, text generation, image synthesis or prompt-based editing."],
  ["05", "Validation and handoff", "Check structure, moderation status and actionability before the generated result becomes a proposal, asset or new version."],
];

const generationModes = [
  {
    label: "AI PROPOSAL",
    title: "From blank page to production-ready concept.",
    text: "AI can create a first concept pack with story framing, contributor angle, capture sequence, slot logic and expected output direction so creation starts with momentum rather than from scratch.",
    image: weddingImage,
  },
  {
    label: "TEXT GENERATION",
    title: "Create structured narrative material instead of free prose only.",
    text: "A brief can be transformed into title candidates, descriptions, contributor instructions, narrative frames, summaries or campaign variants with an output shape that the product can reuse immediately.",
    image: hotelImage,
  },
  {
    label: "IMAGE GENERATION",
    title: "Create visual direction before or alongside capture.",
    text: "Image generation can materialize a mood, composition, use-case mockup or future shot direction. It helps creators align on visual intent before real footage is submitted.",
    image: realEstateImage,
  },
  {
    label: "AI PROMPT EDITING",
    title: "Use natural language as a precise transformation layer.",
    text: "Prompt editing turns a requested change into bounded instructions: target media, requested operation, preserved elements, expected output style and versioned result. The application remains in control of what can be changed.",
    image: footImage,
  },
];

const controls = [
  ["Context boundary", "Only the information relevant to the task is sent, which reduces noise, latency and accidental leakage of unrelated data."],
  ["Structured output contracts", "Generation requests can demand fields, arrays and discrete sections so downstream UI components can consume the result without fragile parsing."],
  ["Task-specific prompting", "Proposal generation, scene planning and prompt editing do not share the same prompt. Each gets a dedicated instruction strategy."],
  ["Synthetic asset separation", "Generated images, derived text and edited outputs remain separate from original media to preserve provenance and auditability."],
  ["Fallback behavior", "If confidence, structure or moderation constraints fail, the AI response can degrade gracefully to a review path instead of silently producing bad output."],
  ["Version-aware review", "Every meaningful transformation can become a reviewable version so creators keep control over what is accepted into the Weave."],
];


const enterpriseGeneration = [
  ["Model routing", "Route each task to the smallest model capable of meeting the required quality instead of paying the same inference cost for every request."],
  ["Synchronous vs asynchronous execution", "Keep interactive generation on the low-latency path while heavier image or batch transformations can run as queued workflow jobs."],
  ["Context budget", "Cap context size and retrieve only what is relevant so token usage, latency and accidental prompt dilution stay under control."],
  ["Output validation", "Validate required fields, moderation status and expected data types before generated content is accepted by downstream UI or automation."],
  ["Asset lineage", "Keep original, AI-generated and AI-edited assets distinguishable so enterprise teams can review provenance and rollback safely."],
  ["Provider portability", "Keep generation logic behind application contracts so model upgrades or provider changes do not force a redesign of product screens and business workflows."],
];

const useCases = [
  ["Story proposal", "Generate the first version of a narrative concept with audience angle, scene logic and capture guidance."],
  ["Weave template proposal", "Convert business context into slot sequences and reusable structures instead of starting from an empty canvas."],
  ["Media prompt editing", "Describe a desired improvement or transformation and let AI produce an edited output under application constraints."],
  ["Campaign variants", "Create alternative framings or localized messaging while preserving the original campaign goal and brand direction."],
];

export default function DualAIGenerationPage() {
  return (
    <AIPageShell
      pageTitle="AI Generation"
      currentPath="/dual-ai/generation"
      eyebrow="WEAVEVO AI / AI GENERATION"
      heroTitle="Generate assets, proposals and transformations from workflow context."
      heroText="Weavevo AI Generation turns a brief, project state and retrieved knowledge into structured creation outputs: concept proposals, narrative text, visual references, template suggestions and prompt-based media edits that remain bounded by product logic."
      finalEyebrow="AI GENERATION"
      finalTitle="Generation is strongest when the product controls the frame."
      finalText="The model becomes a reliable creative engine when context, output structure, versioning and review all remain under application control."
    >
      <section className="dai-section dai-aiGenerationIntro">
        <div className="dai-aiHubSectionLead">
          <span className="dai-sectionEyebrow">RUNTIME PIPELINE</span>
          <h2>Generation is a staged transformation process, not a single prompt box.</h2>
          <p>
            In Weavevo, generative AI sits inside the product runtime. The application decides what the task is,
            what context is available, what output shape is expected and which workflow action can consume the result.
          </p>
        </div>
        <div className="dai-aiRuntimeRail">
          {runtimeSteps.map(([number, title, text]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <AIDiagram kind="creation" />

      <section className="dai-generatedVisualStage" aria-label="Multimodal generation visual">
        <figure className="dai-generatedVisual dai-generatedVisualWide">
          <img src={multimodelImage} alt="Multimodal generation orchestration" loading="lazy" />
        </figure>
      </section>

      <section className="dai-aiGenerationModes">
        <div className="dai-aiGenerationModesInner">
          <div className="dai-aiHubSectionLead dai-aiHubSectionLeadOnDark">
            <span className="dai-sectionEyebrow">GENERATION SURFACES</span>
            <h2>Different creative outcomes can reuse the same orchestration layer.</h2>
            <p>
              Proposal generation, text generation, visual ideation and prompt editing share the same discipline:
              controlled context in, structured workflow artifact out.
            </p>
          </div>
          <div className="dai-aiGenerationEditorialStack">
            {generationModes.map((item, index) => (
              <article className={`dai-aiGenerationEditorial ${index % 2 ? "is-reverse" : ""}`} key={item.label}>
                <figure><img src={item.image} alt="" loading="lazy" /></figure>
                <div>
                  <span className="dai-cardLabel">{item.label}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dai-section dai-aiGenerationControl">
        <div className="dai-aiGenerationControlGrid">
          <div>
            <span className="dai-sectionEyebrow">TECHNICAL CONTROLS</span>
            <h2>Reliable generation comes from contracts, boundaries and reviewability.</h2>
            <p>
              The value is not just that AI can generate. The value is that the generated artifact can be trusted,
              interpreted by the UI and accepted into the workflow with clear provenance.
            </p>
            <Link className="dai-aiTextLink dai-aiTextLinkDark" to="/dual-ai/workflow">
              See how generation fits the full AI workflow <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="dai-aiGenerationControlCards">
            {controls.map(([title, text]) => (
              <article key={title}>
                <strong>{title}</strong>
                <span>{text}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dai-section dai-aiEnterpriseSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">ENTERPRISE GENERATION ARCHITECTURE</span>
          <h2>Generation needs an operating model, not only a creative model.</h2>
          <p>Architecture teams need to know how generation is routed, budgeted, validated, versioned and replaced over time. These controls determine whether AI can scale beyond a demo.</p>
        </div>
        <div className="dai-aiCapabilityMatrix">
          {enterpriseGeneration.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-aiUseCaseRailSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">HIGH-VALUE USE CASES</span>
          <h2>Where AI Generation creates direct product value.</h2>
        </div>
        <div className="dai-aiUseCaseRail">
          {useCases.map(([title, text], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
    </AIPageShell>
  );
}
